//@ts-ignore
import React, {
    Profiler,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    type ReactNode,
    type ProfilerOnRenderCallback
} from "react";

// REMOVED: Waiting react-loosely-lazy release
// import {
//     type EventInfo,
//     ProfilerContext as RLLProfilerContext
// } from "react-loosely-lazy";
// REMOVED
// import { ProfilerContext as RelayProfilerContext } from 'react-relay';
import {
    unstable_scheduleCallback as scheduleCallback,
    unstable_NormalPriority as NormalPriority
} from "scheduler";

import coinflip from "@atlassian/wac-coinflip";
import generateId from "@atlassian/wac-short-id";
import { getInteractionRate } from "@atlassian/wac-ufo-config";
// CHANGED
import UFOInteractionContext, {
    type LabelStack,
    type Label,
    type SegmentLabel
} from "@atlassian/wac-ufo-interaction-context";
import UFOInteractionIDContext, {
    type InteractionIDContextType
} from "@atlassian/wac-ufo-interaction-id-context";
import {
    type CustomTiming,
    type RequestInfo,
    type CustomData,
    addSegment,
    removeSegment,
    abortByNewInteraction,
    addCustomData,
    addHold,
    addHoldByID,
    removeHoldByID,
    addMark,
    addNewInteraction,
    addRequestInfo,
    addSpan,
    tryComplete,
    addProfilerTimings,
    addCustomTiming,
    // extractModuleName,
    addApdex
} from "@atlassian/wac-ufo-interaction-metrics";
import UFORouteName from "@atlassian/wac-ufo-route-name-context";

import type { EnhancedUFOInteractionContextType } from "./types";

type Props = { name: string; children: ReactNode };

/** A portion of the page we apply measurement to */
const UFOSegment = ({ name: segmentName, children }: Props) => {
    const parentContext = useContext(
        UFOInteractionContext
    ) as EnhancedUFOInteractionContextType;
    const segmentId = useMemo(() => generateId(), []);

    const labelStack: LabelStack = useMemo(
        () =>
            parentContext?.labelStack
                ? [
                      ...parentContext.labelStack,
                      { name: segmentName, segmentId }
                  ]
                : [{ name: segmentName, segmentId }],
        [parentContext, segmentName, segmentId]
    );

    // CHANGED
    const interactionId: InteractionIDContextType = useContext(
        UFOInteractionIDContext
    );

    const interactionContext =
        useMemo<EnhancedUFOInteractionContextType>(() => {
            addSegment(labelStack);

            let lastCompleteEndTime = 0;
            function complete(endTime: number = performance.now()) {
                if (interactionId.current) {
                    if (parentContext) {
                        parentContext.complete();
                    } else {
                        const capturedInteractionId = interactionId.current;
                        if (endTime > lastCompleteEndTime) {
                            lastCompleteEndTime = endTime;
                        }

                        scheduleCallback(NormalPriority, () => {
                            if (
                                capturedInteractionId === interactionId.current
                            ) {
                                tryComplete(
                                    interactionId.current,
                                    lastCompleteEndTime
                                );
                            }
                        });
                    }
                }
            }
            function _internalHold(
                this: EnhancedUFOInteractionContextType,
                labelStack: LabelStack,
                name: string
            ): (() => void) | void {
                if (interactionId.current != null) {
                    if (parentContext) {
                        return parentContext._internalHold(labelStack, name);
                    } else {
                        const capturedInteractionId = interactionId.current;
                        const disposeHold = addHold(
                            interactionId.current,
                            labelStack,
                            name
                        );
                        return () => {
                            if (
                                capturedInteractionId === interactionId.current
                            ) {
                                disposeHold();
                            }
                        };
                    }
                }
            }

            function _internalHoldByID(
                this: EnhancedUFOInteractionContextType,
                labelStack: LabelStack,
                id: string,
                name: string,
                remove: boolean
            ): (() => void) | void {
                if (interactionId.current != null) {
                    if (parentContext) {
                        parentContext._internalHoldByID(
                            labelStack,
                            name,
                            id,
                            remove
                        );
                        return;
                    }

                    if (!remove) {
                        addHoldByID(
                            interactionId.current,
                            labelStack,
                            name,
                            id
                        );
                    } else {
                        removeHoldByID(interactionId.current, id);
                    }
                }
            }

            if (parentContext) {
                return {
                    ...parentContext,
                    labelStack,
                    complete
                };
            }

            return {
                labelStack,
                hold(
                    this: EnhancedUFOInteractionContextType,
                    name: string | undefined = "unknown"
                ): void | (() => void) {
                    // eslint-disable-next-line react/no-this-in-sfc
                    return this._internalHold(this.labelStack, name);
                },
                addHoldByID(
                    this: EnhancedUFOInteractionContextType,
                    labelStack: LabelStack,
                    id: string,
                    name: string | undefined = "unknown"
                ) {
                    // eslint-disable-next-line react/no-this-in-sfc
                    this._internalHoldByID(labelStack, id, name, false);
                },
                removeHoldByID(
                    this: EnhancedUFOInteractionContextType,
                    labelStack: LabelStack,
                    id: string,
                    name: string | undefined = "unknown"
                ) {
                    // eslint-disable-next-line react/no-this-in-sfc
                    this._internalHoldByID(labelStack, id, name, true);
                },
                tracePress(
                    this: EnhancedUFOInteractionContextType,
                    name: string | undefined = "unknown",
                    timestamp?: number
                ): void {
                    if (interactionId.current != null) {
                        abortByNewInteraction(interactionId.current, name);
                    }

                    const rate = getInteractionRate(name, "press");

                    if (coinflip(rate)) {
                        const startTimestamp = timestamp ?? performance.now();
                        const newId = generateId();

                        interactionId.current = newId;

                        addNewInteraction(
                            newId,
                            name,
                            "press",
                            startTimestamp,
                            rate,
                            // eslint-disable-next-line react/no-this-in-sfc
                            this.labelStack,
                            UFORouteName.current
                        );
                    }
                },
                retainQuery(
                    this: EnhancedUFOInteractionContextType,
                    info: RequestInfo
                ): void {
                    if (interactionId.current != null) {
                        // eslint-disable-next-line react/no-this-in-sfc
                        addRequestInfo(
                            interactionId.current,
                            this.labelStack,
                            info
                        );
                    }
                },
                addPreload(
                    this: EnhancedUFOInteractionContextType,
                    moduleId: string,
                    timestamp: number
                ) {
                    if (interactionId.current != null) {
                        addMark(
                            interactionId.current,
                            "bundle_preload",
                            moduleId,
                            // eslint-disable-next-line react/no-this-in-sfc
                            this.labelStack,
                            timestamp
                        );
                    }
                },
                addLoad(
                    this: EnhancedUFOInteractionContextType,
                    identifier: string,
                    start: number,
                    end: number
                ) {
                    if (interactionId.current != null) {
                        const { labelStack = [] } = this;
                        addSpan(
                            interactionId.current,
                            "bundle_load",
                            identifier,
                            labelStack,
                            start,
                            end
                        );
                    }
                },
                addMark(
                    this: EnhancedUFOInteractionContextType,
                    name: string,
                    timestamp?: number
                ): void | (() => void) {
                    if (interactionId.current != null) {
                        const time = timestamp ?? performance.now();
                        // eslint-disable-next-line react/no-this-in-sfc
                        addMark(
                            interactionId.current,
                            "custom",
                            name,
                            this.labelStack,
                            time
                        );
                    }
                },
                addCustomData(
                    this: EnhancedUFOInteractionContextType,
                    customData: CustomData
                ) {
                    if (interactionId.current != null) {
                        // eslint-disable-next-line react/no-this-in-sfc
                        addCustomData(
                            interactionId.current,
                            this.labelStack,
                            customData
                        );
                    }
                },
                addCustomTimings(
                    this: EnhancedUFOInteractionContextType,
                    customTimings: CustomTiming
                ) {
                    if (interactionId.current != null) {
                        // eslint-disable-next-line react/no-this-in-sfc
                        addCustomTiming(
                            interactionId.current,
                            this.labelStack,
                            customTimings
                        );
                    }
                },
                addApdex(
                    this: EnhancedUFOInteractionContextType,
                    apdexInfo: { key: string; stopTime: number }
                ) {
                    if (interactionId.current != null) {
                        const { key, stopTime } = apdexInfo;
                        // eslint-disable-next-line react/no-this-in-sfc
                        addApdex(interactionId.current, {
                            key,
                            stopTime,
                            labelStack: this.labelStack
                        });
                    }
                },
                onRender(
                    this: EnhancedUFOInteractionContextType,
                    phase: "mount" | "update",
                    actualDuration: number,
                    baseDuration: number,
                    startTime: number,
                    commitTime: number
                ) {
                    if (interactionId.current !== null) {
                        addProfilerTimings(
                            interactionId.current,
                            // eslint-disable-next-line react/no-this-in-sfc
                            this.labelStack,
                            phase,
                            actualDuration,
                            baseDuration,
                            startTime,
                            commitTime
                        );

                        // eslint-disable-next-line react/no-this-in-sfc
                        this.complete(commitTime);
                    }
                },
                _internalHold,
                _internalHoldByID,
                complete
            };
        }, [parentContext, labelStack, interactionId]);

    const onRender: ProfilerOnRenderCallback = useCallback(
        (
            _id: string,
            phase: "mount" | "update",
            actualDuration: number,
            baseDuration: number,
            startTime: number,
            commitTime: number
        ) => {
            interactionContext.onRender(
                phase,
                actualDuration,
                baseDuration,
                startTime,
                commitTime
            );
        },
        [interactionContext]
    );

    // const relayProfilerContext = useMemo(
    //     () => ({
    //         wrapPrepareQueryResource<T>(cb: () => T): T {
    //             return cb();
    //         },
    //         retainQuery(info: RequestInfo) {
    //             interactionContext.retainQuery(info);
    //         }
    //     }),
    //     [interactionContext]
    // );

    useEffect(
        () => () => {
            removeSegment(labelStack);
        },
        [interactionId, parentContext, interactionContext, labelStack]
    );

    // const reactLooselyLazyContext = useMemo(() => {
    //     const moduleLoadingRequests: {
    //         [key: string]: {
    //             start: number;
    //             timeoutId: ReturnType<typeof setTimeout> | number | undefined;
    //         };
    //     } = {};
    //     return {
    //         current: {
    //             onPreload(moduleId: string, _priority?: number) {
    //                 interactionContext.addPreload(
    //                     extractModuleName(moduleId),
    //                     performance.now()
    //                 );
    //             },
    //             onLoadStart(info: EventInfo): void {
    //                 const timeoutId = setTimeout(() => {
    //                     delete moduleLoadingRequests[info.identifier];
    //                 }, 30000);
    //                 const request = {
    //                     start: performance.now(),
    //                     timeoutId
    //                 };
    //                 moduleLoadingRequests[info.identifier] = request;
    //             },
    //             onLoadComplete(info: EventInfo): void {
    //                 const request = moduleLoadingRequests[info.identifier];
    //                 if (request) {
    //                     clearTimeout(request.timeoutId);
    //                     delete moduleLoadingRequests[info.identifier];
    //                     interactionContext?.addLoad(
    //                         extractModuleName(info.identifier),
    //                         request.start,
    //                         performance.now()
    //                     );
    //                 }
    //             },
    //             placeholderFallBackMounted(
    //                 id: string,
    //                 name?: string,
    //                 lazyWait?: boolean
    //             ): void {
    //                 if (lazyWait) {
    //                     return;
    //                 }
    //                 interactionContext.addHoldByID(labelStack, id, name);
    //             },
    //             placeholderFallBackUnmounted(
    //                 id: string,
    //                 name?: string,
    //                 lazyWait?: boolean
    //             ): void {
    //                 if (lazyWait) {
    //                     return;
    //                 }
    //                 interactionContext.removeHoldByID(labelStack, id, name);
    //             }
    //         }
    //     };
    // }, [interactionContext, labelStack]);

    // CHANGED
    const reactProfilerId = useMemo(
        () => labelStack.map((l: Label | SegmentLabel) => l.name).join("/"),
        [labelStack]
    );

    return (
        <UFOInteractionContext.Provider value={interactionContext}>
            <Profiler id={reactProfilerId} onRender={onRender}>
                {/* <RLLProfilerContext.Provider value={reactLooselyLazyContext}> */}
                {children}
                {/* </RLLProfilerContext.Provider> */}
            </Profiler>
        </UFOInteractionContext.Provider>
    );
};
export default UFOSegment;
